import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Types
import type { DynamicVariableChannelData } from 'types';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import { clearPageValidation } from 'store/slices/pageValidation';
import { selectUserActivePartnerTimeZone } from 'store/slices/userData';
import { clearTemplateList, selectTemplateState } from 'store/slices/template';
// Import Components
import PageHeadline from 'components/common/PageHeadline';
import CommonTemplate from './CommonTemplate';
import PredefinedTemplatePreSaveModalBodyContent from './PredefinedTemplatePreSaveModalBodyContent';
import { createNotificationSaveResult } from './templateWrapper.utils';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useToaster from 'hooks/UseToaster';
import useGoToRoute from 'hooks/GoToRoute';
import useQueryParams from 'hooks/UseQueryParams';
import useTemplatePresaveValidationProperties from 'hooks/UseTemplatePresaveValidationProperties';
// Import Services
import { createTemplateResultData, getTemplateValidationTranslatableErrorText } from 'services/template';
// Import Enums
import { TemplateTypeEnum, ObjectDefinitionTypeEnum } from 'enums';
// Import Constants
import { AlertTypes, l } from 'constants/common';
// Import Styles
import 'assets/scss/commonTemplateWrapper.scss';

const { success } = AlertTypes;

const CommonTemplateWrapper = (props) => {
    const {
        editOrCreateRequest,
        resetValidationScenario,
        titleKey,
        nameAndDescriptionDefaultValue,
        isLoading,
        templateType,
        addValidationProperties,
        removeValidationProperties,
        maxSMSPartsCount,
        shortenerAndTrackerSettings,
        getAssets,
        addAsset,
        definitionType,
        initialPredefinedOptions,
    } = props;
    const { t } = useTranslation();
    const { goToRoute } = useGoToRoute();
    const { showToaster } = useToaster();
    const { doPostRequest } = useRequest();
    const dispatch = useAppDispatch();

    const partnerTimeZone = useAppSelector(selectUserActivePartnerTimeZone) ?? 0;

    const templateData = useAppSelector(selectTemplateState);
    const [tabActiveKey, setTabActiveKey] = useState<string | number>('0');
    const [templateDynamicVariables, setTemplateDynamicVariables] =
        useState<Nullable<DynamicVariableChannelData[]>>(null);
    // PreSave Modal States
    const [isValidPreSaveModalContent, setIsValidPreSaveModalContent] = useState(false);
    const [predefinedOptions, setPredefinedOptions] = useState(
        initialPredefinedOptions ?? { regions: [], categories: [] },
    );

    const queryParams = useQueryParams('notification');
    const decodedQueryParams = queryParams.decode();
    const eventId = decodedQueryParams?.eventId;
    const selectedNotificationChannel = decodedQueryParams?.selectedChannel;

    const additionalValidationProperties = useTemplatePresaveValidationProperties(
        templateDynamicVariables,
        templateType,
    );

    const saveHandler = (name: string, description: string) => {
        const successToasterMessage =
            definitionType === ObjectDefinitionTypeEnum.Regular
                ? l.TemplateWasSuccessfullySaved
                : l.PredefinedTemplateWasSuccessfullySaved;
        return doPostRequest(editOrCreateRequest, {
            requestBody: createTemplateResultData(
                name,
                description,
                templateData,
                templateType,
                eventId,
                templateDynamicVariables,
                definitionType,
                predefinedOptions,
                partnerTimeZone,
            ),
            successCallback: () => showToaster(success, t(successToasterMessage)),
        }).then((data) =>
            createNotificationSaveResult({
                data,
                eventId,
                selectedNotificationChannel,
                queryParams,
                goToRoute,
            }),
        );
    };

    const resetHandler = () => {
        dispatch(clearTemplateList({ templateType }));
        resetValidationScenario();
        setTabActiveKey(0);
    };

    const cleanUp = () => {
        return () => {
            resetHandler();
            dispatch(clearPageValidation());
        };
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(cleanUp, []);

    const onPredefinedContentChange = (data) => {
        setPredefinedOptions(data.predefined);
    };

    const onPreSaveModalValidationChange = (isValid) => {
        setIsValidPreSaveModalContent(isValid);
    };

    useEffect(() => {
        initialPredefinedOptions && setPredefinedOptions(initialPredefinedOptions);
    }, [initialPredefinedOptions]);

    return (
        <div className="crm-create-template-page-wrapper-container sms-template-wrap">
            <PageHeadline
                titleKey={titleKey}
                handleSave={saveHandler}
                handleReset={resetHandler}
                defaultName={nameAndDescriptionDefaultValue?.Name}
                defaultDescription={nameAndDescriptionDefaultValue?.Description}
                getCustomValidationTypeErrorText={getTemplateValidationTranslatableErrorText(t)}
                isLoading={isLoading}
                withWarningsAlertContent={true}
                additionalValidationProperties={additionalValidationProperties}
                {...(definitionType === ObjectDefinitionTypeEnum.Predefined && {
                    preSaveModalContent: (
                        <PredefinedTemplatePreSaveModalBodyContent
                            getUpdatedValues={onPredefinedContentChange}
                            getIsFormValid={onPreSaveModalValidationChange}
                            initialDataValues={{ predefined: predefinedOptions }}
                        />
                    ),
                    isValidPreSaveModalContent: isValidPreSaveModalContent,
                })}
            />
            <CommonTemplate
                addValidationProperties={addValidationProperties}
                removeValidationProperties={removeValidationProperties}
                maxSMSPartsCount={maxSMSPartsCount}
                isLoading={isLoading}
                tabActiveKey={tabActiveKey}
                setTabActiveKey={setTabActiveKey}
                templateType={templateType}
                getAssets={getAssets}
                addAsset={addAsset}
                eventId={eventId}
                shortenerAndTrackerSettings={shortenerAndTrackerSettings}
                templateDynamicVariables={templateDynamicVariables}
                setTemplateDynamicVariables={setTemplateDynamicVariables}
            />
        </div>
    );
};

CommonTemplateWrapper.propTypes = {
    titleKey: PropTypes.string.isRequired,
    editOrCreateRequest: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    templateType: PropTypes.string,
    nameAndDescriptionDefaultValue: PropTypes.object,
    addValidationProperties: PropTypes.func,
    removeValidationProperties: PropTypes.func,
    resetValidationScenario: PropTypes.func,
    maxSMSPartsCount: PropTypes.number,
    getAssets: PropTypes.func,
    addAsset: PropTypes.func,
    shortenerAndTrackerSettings: PropTypes.array,
    definitionType: PropTypes.number,
    initialPredefinedOptions: PropTypes.object,
};

CommonTemplateWrapper.defaultProps = {
    isLoading: false,
    templateType: TemplateTypeEnum.SMS,
    addValidationProperties: noop,
    removeValidationProperties: noop,
    resetValidationScenario: noop,
    getAssets: noop,
    addAsset: noop,
    shortenerAndTrackerSettings: [],
};

export default CommonTemplateWrapper;
